import React from "react";
import { useTheme } from "react-jss";
import classnames from "classnames";
import { kebabCase } from "../../../utils/lodash";
import { RendererProps } from "../BaseRenderer";
import { QuestionType, isGrid } from "../../../models/enums/QuestionType";
import ValidationError from "../../shared/ValidationError";
import { ValidationResultModel } from "../../../models/ValidationResultModel";
import { isNullOrEmpty } from "../../../utils";
import useStyles from "./QuestionRenderer.styles";
import { translate } from "../../../models/utilities/translationUtilities";
import useDimensionContext from "../../../hooks/useDimensionContext";
import ScrollableContainer from "../../shared/ScrollableContainer";
import { DebugInfo } from "../../debug/DebugInfo";
import useRecall from "../../../hooks/useRecall";
import { RecalledText } from "../../../containers/shared/RecalledText";
import { useElementDimensions } from "../../../hooks/useElementDimensions";
import { StoreState } from "../../../models/states/StoreState";
import { useSelector } from "react-redux";

interface Props extends RendererProps {
    isHidden?: boolean;
    control: any;
    language: string;
    validationResult?: ValidationResultModel;
}

const getTitle = ({ question, language }) => translate(question, "TITLE", language);
const getText = ({ question, language }) => translate(question, "TEXT", language);
const getShortText = ({ question, language }) => translate(question, "SHORT_TEXT", language);

function typeClass({ question }) {
    const classes = QuestionType[question.type];
    return `question--type-${kebabCase(classes)}`;
}

function ErrorMessageContainer({ question, questionnaire, language, validationResult }) {
    const theme = useTheme();
    const classes = useStyles({ theme });
    const firstErrorVariable = Object.keys(validationResult.errorsByVariableName)[0];
    const variableErrors = validationResult.errorsByVariableName[firstErrorVariable];
    if (variableErrors) {
        const firstError = variableErrors[0];
        if (firstError) {
            return (
                <div className={classes.questionValidation}>
                    <ValidationError
                        question={question}
                        key={firstError}
                        questionnaire={questionnaire}
                        language={language}
                        error={firstError}
                    />
                </div>
            );
        }
    }
    return <div className={classes.questionValidation} />;
}

function QuestionTitle({ question, title, classes }) {
    const recalledTitle = useRecall(title);
    return (
        <h2
            id={`${question.elementId}_TITLE`}
            className={classes.questionTitle}
            dangerouslySetInnerHTML={{ __html: recalledTitle }}
        />
    );
}

function RecalledQuestionLabel({ text }) {
    const ref = React.useRef<HTMLDivElement>();
    const { innerHeight } = useElementDimensions(ref);
    const [minHeight, setMinHeight] = React.useState(0);

    React.useLayoutEffect(() => {
        if (innerHeight > minHeight) {
            setMinHeight(innerHeight);
        }
    }, [innerHeight, minHeight]);

    // RecalledText gets removed and added back from dom on iOS and that causes critical layout issues
    // Setting min height fixes this issue
    return (
        <div ref={ref} style={{ minHeight }}>
            <RecalledText text={text} />
        </div>
    );
}

export function QuestionLabel({ question, text, shortText, classes }) {
    const device = useSelector((state: StoreState) => state.interview.device);
    const isIOS = ["ios", "mac_os_x", "mac_os"].includes(device?.os?.toLowerCase());
    return (
        <div id={`${question.elementId}_LABEL`} className={classes.questionText}>
            {isIOS ? <RecalledQuestionLabel text={text} /> : <RecalledText text={text} />}
            {!isGrid(question.type) && !isNullOrEmpty(shortText) && (
                <>
                    <br />
                    <RecalledText
                        text={shortText}
                        render={(recalled) => (
                            <p
                                className={classes.questionShortText}
                                dangerouslySetInnerHTML={{ __html: recalled }}
                            />
                        )}
                    />
                </>
            )}
        </div>
    );
}

function QuestionContent({ question, control, classes }) {
    let content = (
        <>
            {control}
            <div className="clear" />
        </>
    );
    if (isGrid(question.type)) {
        content = <ScrollableContainer>{content}</ScrollableContainer>;
    }
    return <div className={classes.questionContent}>{content}</div>;
}

export default function QuestionRenderer(props: Props) {
    const theme = useTheme();
    const classes = useStyles({ theme });
    const dimension = useDimensionContext();
    const isDisplayWide = dimension && dimension.minWidth(778);

    const title = getTitle(props);
    const text = getText(props);
    const shortText = getShortText(props);

    const { questionnaire, question, language, isHidden, control, validationResult } = props;

    const isValid = !validationResult || validationResult.isValid;
    const isTitleVisible = !isNullOrEmpty(title);

    return (
        <div
            id={question.elementId}
            className={classnames(
                classes.question,
                typeClass(props),
                !isValid && classes.questionError,
            )}
            style={{ display: isHidden ? "none" : undefined }}
        >
            <DebugInfo>{question.name}</DebugInfo>
            {!isValid && (
                <ErrorMessageContainer
                    question={question}
                    questionnaire={questionnaire}
                    language={language}
                    validationResult={validationResult}
                />
            )}
            <div
                className={classnames(
                    !isValid && classes.questionValidationFocus,
                    !isValid && isDisplayWide && classes.dynamicQuestionValidationFocus,
                )}
            >
                <fieldset
                    aria-invalid={!isValid}
                    aria-describedby={isValid ? undefined : `${question.elementId}_ERROR`}
                >
                    <legend className={classes.questionLegend}>
                        {isTitleVisible && (
                            <QuestionTitle question={question} title={title} classes={classes} />
                        )}
                        <QuestionLabel
                            question={question}
                            text={text}
                            shortText={shortText}
                            classes={classes}
                        />
                    </legend>
                    <QuestionContent question={question} control={control} classes={classes} />
                </fieldset>
            </div>
        </div>
    );
}
